import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DragDropModule, CdkDragMove } from '@angular/cdk/drag-drop';
import { Document, DocumentUsage } from '../model';
import { PreviewDocument } from '../common/service/document.service';
import { TranslateModule } from '@ngx-translate/core';
import { ManagedDocument } from '../common/models/document.model';
import { AppState } from '../common';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';

export type DocumentUploadedSortField = 'status' | 'title' | 'type' | 'date' | 'size';
type TableRow = Document | ManagedDocument;

@Component({
  selector: 'app-document-list-table',
  standalone: true,
  templateUrl: './document-list-table.component.html',
  styleUrls: ['./document-list-table.component.scss'],
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    DragDropModule,
    TranslateModule,
    MatProgressSpinnerModule,
  ],
})
export class DocumentListTableComponent implements OnDestroy {
  /** Mirrors the server's `Company.mayWrite` — see `CompanyWriteAccess`. */
  readonly readOnly = inject(CompanyWriteAccess).readOnly;

  private static nextDropListId = 0;
  private readonly appState = inject(AppState);
  @Input() documents: TableRow[] = [];
  @Input() previewMap: Record<string, PreviewDocument> = {};
  @Input() localPreviewMap: Record<string, string> = {};
  @Input() total = 0;
  @Input() pageIndex = 0;
  @Input() pageSize = 20;
  @Input() sortField: DocumentUploadedSortField = 'date';
  @Input() sortDirection: 'asc' | 'desc' = 'desc';
  @Input() selectable = false;
  @Input() selectedId: string | null = null;
  @Input() highlightedIds: Set<string> = new Set<string>();
  @Input() mode: 'local' | 'uploaded' = 'uploaded';
  @Input() disableActions = false;
  @Input() hideActions = false;
  @Input() statusLabel = 'Status';
  @Input() titleLabel = 'Title';
  @Input() typeLabel = 'Type';
  @Input() dateLabel = 'Date';
  @Input() sizeLabel = 'Size';
  @Input() sizeFormatter: (size: number) => string = (size) => `${size ?? 0} B`;
  @Input() emptyTitle = 'No uploaded documents found';
  @Input() emptySubtitle = 'Upload files to see them here.';
  @Input() emptyStateTestId?: string;
  @Input() previewHoverEnabled = false;
  @Input() rowTooltip?: (document: TableRow) => string | null;
  @Input() dragEnabled = false;
  @Input() dragDropListId = `document-list-${++DocumentListTableComponent.nextDropListId}`;
  @Input() dragDropConnections: string[] = [];
  @Input() usagePresentation: 'chips' | 'count' = 'chips';
  @Input() openUsageInNewTab = false;

  @Output() sortChange = new EventEmitter<DocumentUploadedSortField>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() rowSelect = new EventEmitter<TableRow>();
  @Output() viewDocument = new EventEmitter<TableRow>();
  @Output() editDocument = new EventEmitter<TableRow>();
  @Output() deleteDocument = new EventEmitter<TableRow>();
  @Output() uploadDocument = new EventEmitter<ManagedDocument>();
  @Output() navigateUsage = new EventEmitter<DocumentUsage>();
  @Output() rowDragMoved = new EventEmitter<{document: TableRow; event: CdkDragMove}>();
  private readonly objectUrlMap = new Map<string, string>();
  private readonly expandedUsageIds = new Set<string>();

  previewSrc(document: TableRow) {
    if (this.isLocalDocument(document)) {
      if (document.kind === 'image') {
        if (document.dataUrl) {
          return document.dataUrl;
        }
        if (document.blob) {
          if (!this.objectUrlMap.has(document.id)) {
            this.objectUrlMap.set(document.id, URL.createObjectURL(document.blob));
          }
          return this.objectUrlMap.get(document.id);
        }
        return undefined;
      }
      return this.localPreviewMap[document.id] ?? undefined;
    }
    const preview = this.previewMap[document.id]?.preview;
    return preview ? `data:image/png;base64,${preview}` : undefined;
  }

  sortIcon(field: DocumentUploadedSortField) {
    if (this.sortField !== field) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  handleSort(field: DocumentUploadedSortField) {
    this.sortChange.emit(field);
  }

  handlePage(event: PageEvent) {
    this.pageChange.emit(event);
  }

  handleRowClick(document: TableRow) {
    if (this.selectable) {
      this.rowSelect.emit(document);
    } else {
      this.viewDocument.emit(document);
    }
  }

  handleEdit(document: TableRow, event: Event) {
    event.stopPropagation();
    this.editDocument.emit(document);
  }

  handleDelete(document: TableRow, event: Event) {
    event.stopPropagation();
    this.deleteDocument.emit(document);
  }

  handlePreviewView(document: TableRow, event: Event) {
    event.stopPropagation();
    this.viewDocument.emit(document);
  }

  handleUpload(document: TableRow, event: Event) {
    event.stopPropagation();
    if (this.isLocalDocument(document)) {
      this.uploadDocument.emit(document);
    }
  }

  handleDragMoved(document: TableRow, event: CdkDragMove) {
    this.rowDragMoved.emit({document, event});
  }

  fallbackIcon(document: TableRow) {
    if (this.isLocalDocument(document) && document.kind === 'zip') {
      return 'folder_zip';
    }
    return 'insert_drive_file';
  }

  hasPreviewError(document: TableRow) {
    if (this.isLocalDocument(document)) {
      return false;
    }
    return !!this.previewMap[document.id]?.error_type;
  }

  supportMailto(document: TableRow): string {
    const subject = `Preview unavailable: ${document.title || document.id}`;
    const body = [
      'Hello Support Team,',
      '',
      'The document preview is unavailable.',
      '',
      `Document ID: ${document.id ?? 'n/a'}`,
      `Title: ${document.title ?? 'n/a'}`,
      `Type: ${this.documentTypeName(document) ?? 'n/a'}`,
      `Date: ${this.documentDate(document) ?? 'n/a'}`,
      '',
      'Please help investigate this issue.',
    ].join('\n');
    return `mailto:support@supplycanvas.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  isLocalDocument(document: TableRow): document is ManagedDocument {
    const managed = document as ManagedDocument;
    return managed.dataUrl !== undefined || managed.blob !== undefined;
  }

  documentTypeName(document: TableRow) {
    return this.isLocalDocument(document) ? document.documentTypeName : document.type?.name;
  }

  documentDate(document: TableRow) {
    if (this.isLocalDocument(document)) {
      return document.documentDate ?? document.createdAt;
    }
    return document.date;
  }

  documentSize(document: TableRow) {
    return this.isLocalDocument(document) ? document.size : undefined;
  }

  isSelected(document: Document) {
    return this.selectable && this.selectedId === document.id;
  }

  isHighlighted(document: TableRow): boolean {
    return this.highlightedIds.has(document.id);
  }

  documentUsages(document: TableRow): DocumentUsage[] {
    if (this.mode !== 'uploaded' || this.isLocalDocument(document)) return [];
    return document.usages ?? [];
  }

  hasUsages(document: TableRow): boolean {
    return this.documentUsages(document).length > 0;
  }

  usageCount(document: TableRow): number {
    return this.documentUsages(document).length;
  }

  usageCountLabel(document: TableRow): string {
    return this.usageCount(document) === 1 ? 'usage' : 'usages';
  }

  isUsageExpanded(document: TableRow): boolean {
    return this.expandedUsageIds.has(document.id);
  }

  toggleUsageList(document: TableRow, event: Event): void {
    event.stopPropagation();
    if (this.expandedUsageIds.has(document.id)) {
      this.expandedUsageIds.delete(document.id);
    } else {
      this.expandedUsageIds.add(document.id);
    }
  }

  usageIcon(usage: DocumentUsage): string {
    switch (usage.type) {
      case 'chain':
      case 'chain_pending':
        return 'link';
      case 'product_attribute':
      case 'product_element':
        return 'inventory_2';
      case 'report':
      case 'report_detail_product':
      case 'report_detail_article':
      case 'report_detail_article_note':
      case 'report_product_notes':
      case 'attribute_report_notes':
        return 'assessment';
      case 'company':
        return 'apartment';
      case 'site':
      case 'site_manual':
        return 'forest';
      default:
        return 'link';
    }
  }

  usageLabel(usage: DocumentUsage): string {
    const label = usage.label ?? '';
    switch (usage.type) {
      case 'chain':
      case 'chain_pending':
        return label ? `Chain: ${label}` : 'Chain';
      case 'product_attribute':
        return label ? `Attribute: ${label}` : 'Attribute';
      case 'product_element':
        return label ? `Packaging element: ${label}` : 'Packaging element';
      case 'report':
      case 'report_detail_product':
      case 'report_detail_article':
      case 'report_detail_article_note':
      case 'report_product_notes':
      case 'attribute_report_notes':
        return label ? `Report: ${label}` : 'Report';
      case 'company':
        return label ? `Company: ${label}` : 'Company';
      case 'site':
      case 'site_manual':
        return label ? `Site: ${label}` : 'Site';
      default:
        return label || usage.type;
    }
  }

  handleUsageClick(usage: DocumentUsage, event: Event): void {
    event.stopPropagation();
    if (this.openUsageInNewTab) {
      const url = this.usageUrl(usage);
      if (url) {
        window.open(url, '_blank', 'noopener');
      }
      return;
    }
    this.navigateUsage.emit(usage);
  }

  private usageUrl(usage: DocumentUsage): string | null {
    const cs = this.appState.companySite$.value;
    if (!cs?.company) return null;
    const companyId = cs.company.id;
    const siteId = cs.site?.id ?? cs.company.sites?.[0]?.id;
    if (!siteId) return null;
    return `/company/${companyId}/site/${siteId}/${usage.link}`;
  }

  ngOnDestroy(): void {
    for (const url of this.objectUrlMap.values()) {
      URL.revokeObjectURL(url);
    }
    this.objectUrlMap.clear();
  }
}
